/**
 * PinnedSidePanel — правый side-panel со списком закреплённых.
 * Рендерится как часть chat-shell grid'а (как MessengerRightPanel).
 * Открытие → data-panel-open="true" + grid добавляет 4-ю колонку 360px.
 * Чат подвигается, не оверлей.
 */
import { useQuery } from "@tanstack/react-query";

import { Icon } from "@/components/prototype/Icon";
import { fetchChatPinned, type ChatMessage } from "@/api/dashboard";


export function PinnedSidePanel({
  topicId, isOpen, onClose, onJumpToMessage,
}: {
  topicId: number | null;
  isOpen: boolean;
  onClose: () => void;
  onJumpToMessage: (id: number) => void;
}) {
  const q = useQuery({
    queryKey: ["chat/pinned", topicId],
    queryFn: () => topicId ? fetchChatPinned(topicId) : Promise.resolve([]),
    enabled: !!topicId && isOpen,
  });

  if (!isOpen) return null;
  const items = q.data || [];

  return (
    <aside className="mng-right-panel pinned-side-panel">
      <header className="mng-rp-header">
        <div className="mng-rp-tabs">
          <button className="mng-rp-tab active" type="button">
            Закреплённые{items.length > 0 ? ` · ${items.length}` : ""}
          </button>
        </div>
        <button className="mng-rp-close" onClick={onClose} aria-label="Закрыть" type="button">
          <Icon name="x" size={16}/>
        </button>
      </header>
      <div className="mng-rp-content pinned-side-content">
        {q.isLoading && <div className="pinned-modal-empty">Загрузка…</div>}
        {!q.isLoading && items.length === 0 && (
          <div className="pinned-modal-empty">Нет закреплённых сообщений</div>
        )}
        {items.map((m) => (
          <PinnedItem key={m.id} m={m}
            onClick={() => { onJumpToMessage(m.id); }}/>
        ))}
      </div>
    </aside>
  );
}


function PinnedItem({ m, onClick }: { m: ChatMessage; onClick: () => void }) {
  const ts = m.created_at ? formatPinnedDate(m.created_at) : "";
  const author = m.author_name || "Без автора";
  const preview = m.text || (m.attachments && m.attachments.length > 0
    ? `📎 ${m.attachments[0].filename || "файл"}` : "—");
  return (
    <button type="button" className="pinned-modal-item" onClick={onClick}>
      <div className="pinned-modal-item-bar"/>
      <div className="pinned-modal-item-body">
        <div className="pinned-modal-item-head">
          <span className="pinned-modal-item-author">{author}</span>
          <span className="pinned-modal-item-ts">{ts}</span>
        </div>
        <div className="pinned-modal-item-text">{preview}</div>
      </div>
    </button>
  );
}

function formatPinnedDate(iso: string): string {
  const isoZ = iso.endsWith("Z") ? iso : (iso.includes("T") ? iso + "Z" : iso.replace(" ", "T") + "Z");
  const d = new Date(isoZ);
  if (Number.isNaN(d.getTime())) return "";
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  const time = `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  if (sameDay) return time;
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${dd}.${mm} ${time}`;
}
